import React, { Fragment, useEffect, useRef, useState } from 'react';
import {
  Typography,
  Button,
  Form,
  message,
  Input,
  Switch,
  DatePicker,
  Modal,
  ConfigProvider,
  Slider,
  Avatar,
} from 'antd';
import { UserOutlined } from '@ant-design/icons';
import Cropper from 'react-easy-crop';
import locale from 'antd/es/locale/ja_JP';
import dayjs from 'dayjs';
import Axios from 'axios';
import getCroppedImg from './cropImage';

const { Title, Text } = Typography;
const { TextArea } = Input;

// アップロード可能な画像形式とサイズ
const ALLOWED_MIME = ['image/jpeg', 'image/jpg', 'image/png'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

// 講師の新規登録／編集画面
const MakeInstructorPage = (props) => {
  const [value, setValue] = useState({});
  const [mode, setMode] = useState('new');
  const [originalEmail, setOriginalEmail] = useState('');
  // アイコン差し替え時に古い S3 オブジェクトを掃除するための保持
  const [oldPhotoKey, setOldPhotoKey] = useState(null);
  // クロップモーダル関連
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const iInfo = props.location.state || {};
  const { user } = props;

  useEffect(() => {
    if (!user.userData) return null;
    if (iInfo._id) {
      setMode('edit');
      setOriginalEmail(iInfo.email || '');
      setValue({
        _id: iInfo._id,
        name: iInfo.name || '',
        email: iInfo.email || '',
        title: iInfo.title || '',
        sector: iInfo.sector || '',
        contract_date: iInfo.contract_date || null,
        contract_status: iInfo.contract_status || false,
        description: iInfo.description || '',
        profile: iInfo.profile || '',
        photo_url: iInfo.photo_url || '',
        photo_key: iInfo.photo_key || '',
      });
    } else {
      setValue({ contract_status: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // 各フィールドのonChange
  const onChangeName = (e) =>
    setValue({ ...value, name: e.currentTarget.value });
  const onChangeEmail = (e) =>
    setValue({ ...value, email: e.currentTarget.value });
  const onChangeTitle = (e) =>
    setValue({ ...value, title: e.currentTarget.value });
  const onChangeSector = (e) =>
    setValue({ ...value, sector: e.currentTarget.value });
  const onChangeDescription = (e) =>
    setValue({ ...value, description: e.currentTarget.value });
  const onChangeProfile = (e) =>
    setValue({ ...value, profile: e.currentTarget.value });
  const onChangeContractStatus = (checked) =>
    setValue({ ...value, contract_status: checked });
  const onChangeContractDate = (date, dateString) =>
    setValue({ ...value, contract_date: dateString || null });

  // 実際の保存処理
  const doSave = async () => {
    try {
      // 必須チェック
      if (!value.name || !value.name.trim()) {
        message.error('講師名は必須です');
        return;
      }

      let res = {};
      if (mode === 'edit') {
        // アイコン差し替え時：旧 S3 オブジェクトをサーバー側で掃除させるため _oldPhotoKey を同送
        const submitBody = { ...value };
        if (oldPhotoKey && oldPhotoKey !== value.photo_key) {
          submitBody._oldPhotoKey = oldPhotoKey;
        }
        res = await Axios.put(`/api/instructor/${value._id}`, submitBody);
      } else {
        res = await Axios.post('/api/instructor', value);
      }

      if (res.data.success) {
        message.success({
          content:
            mode === 'edit' ? '講師情報を更新しました' : '講師を登録しました',
          key: 'submit',
          duration: 2,
        });
        setTimeout(() => {
          props.history.push('/instructormgr');
        }, 800);
      } else {
        throw new Error('保存に失敗しました');
      }
    } catch (error) {
      message.error({
        content: '保存に失敗しました',
        key: 'submit',
        duration: 2,
      });
      console.log({ error });
    }
  };

  // 保存ボタン押下：編集モードでメール変更時は確認モーダルを出す
  const onSubmit = async () => {
    if (
      mode === 'edit' &&
      value.email !== originalEmail &&
      originalEmail
    ) {
      Modal.confirm({
        title: '確認',
        content:
          'この講師は既存の動画／記事から参照されている可能性があります。メールアドレスを変更すると、記事側の instructor_email 表示と整合が崩れる場合があります。続行しますか？',
        okText: '変更して保存',
        cancelText: 'キャンセル',
        onOk: async () => {
          await doSave();
        },
      });
    } else {
      await doSave();
    }
  };

  const onCancel = () => {
    props.history.push('/instructormgr');
  };

  // ─── アイコン画像：選択 → クロップ → S3 アップロード ───

  // ファイル選択ボタンクリック
  const onPickIcon = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  // ファイルが選択された
  const onFileSelected = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    if (!ALLOWED_MIME.includes(file.type)) {
      message.error('JPEG または PNG を選択してください');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      message.error('ファイルサイズは10MB以下にしてください');
      return;
    }

    // ローカルプレビュー用 ObjectURL を生成してモーダルを開く
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setCropModalOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const onCropComplete = (_, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  };

  // 「完了」: クロップ結果を S3 にアップロード
  const onCropDone = async () => {
    if (!imageSrc || !croppedAreaPixels) {
      message.error('画像範囲を確認してから完了してください');
      return;
    }
    try {
      setUploading(true);

      // 1) クロップ画像を Blob として生成
      const blob = await getCroppedImg(imageSrc, croppedAreaPixels);

      // 2) Presigned URL を取得
      const psRes = await Axios.post('/api/presignedUrl/instructor', {
        fileName: `icon_${Date.now()}.jpg`,
        fileType: 'image/jpeg',
        fileSize: blob.size,
      });

      if (!psRes.data || !psRes.data.success) {
        throw new Error('Presigned URL の取得に失敗しました');
      }
      const { uploadUrl, fileUrl, s3Key } = psRes.data;

      // 3) S3 へ直接 PUT
      const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        body: blob,
        headers: { 'Content-Type': 'image/jpeg' },
      });
      if (!putRes.ok) {
        throw new Error('S3 アップロードに失敗しました');
      }

      // 4) 旧 photo_key があれば差し替え対象として記憶
      if (value.photo_key && value.photo_key !== s3Key) {
        // 既に編集モードで残っている旧キー、または同一セッションで作った前回キー
        setOldPhotoKey(value.photo_key);
      }

      // 5) state を更新
      setValue({
        ...value,
        photo_url: `${fileUrl}?v=${Date.now()}`, // 表示キャッシュ回避
        photo_key: s3Key,
      });

      message.success('アイコンをアップロードしました');
      setCropModalOpen(false);
      setImageSrc(null);
    } catch (error) {
      console.log(error);
      message.error('アイコンのアップロードに失敗しました');
    } finally {
      setUploading(false);
    }
  };

  // 「画像を削除」ボタン
  const onRemovePhoto = () => {
    if (!value.photo_url && !value.photo_key) return;
    Modal.confirm({
      title: 'アイコンを削除しますか？',
      content:
        '保存後に旧画像は S3 からも削除されます。キャンセルすると元に戻せません。',
      okText: '削除',
      cancelText: 'キャンセル',
      okButtonProps: { danger: true },
      onOk: () => {
        if (value.photo_key) {
          setOldPhotoKey(value.photo_key);
        }
        setValue({ ...value, photo_url: '', photo_key: '' });
      },
    });
  };

  return (
    <Fragment>
      <div style={{ maxWidth: '640px', margin: '2rem auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <Title level={2}>
            {mode === 'edit' ? '講師編集' : '講師新規登録'}
          </Title>
        </div>
        <Form onSubmit={onSubmit}>
          <Text strong>■ 講師名（必須）</Text>
          <div style={{ height: 4 }} />
          <Input
            placeholder="講師名"
            allowClear
            value={value.name || ''}
            onChange={onChangeName}
          />
          <br />
          <br />

          <Text strong>■ メールアドレス</Text>
          <div style={{ height: 4 }} />
          <Input
            placeholder="example@domain.com"
            allowClear
            value={value.email || ''}
            onChange={onChangeEmail}
          />
          <br />
          <br />

          <Text strong>■ 肩書</Text>
          <div style={{ height: 4 }} />
          <Input
            placeholder="例: 代表講師 / シニアコンサルタント"
            allowClear
            value={value.title || ''}
            onChange={onChangeTitle}
          />
          <br />
          <br />

          <Text strong>■ 専門分野</Text>
          <div style={{ height: 4 }} />
          <Input
            placeholder="例: 人事評価 / リーダーシップ"
            allowClear
            value={value.sector || ''}
            onChange={onChangeSector}
          />
          <br />
          <br />

          <Text strong>■ 契約日</Text>
          <div style={{ height: 4 }} />
          <ConfigProvider locale={locale}>
            <DatePicker
              value={value.contract_date ? dayjs(value.contract_date) : null}
              onChange={onChangeContractDate}
              format="YYYY-MM-DD"
              style={{ width: 240 }}
            />
          </ConfigProvider>
          <br />
          <br />

          <Text strong>■ 契約の有効性</Text>
          <div style={{ height: 4 }} />
          <Switch
            checked={!!value.contract_status}
            onChange={onChangeContractStatus}
          />{' '}
          <Text>{value.contract_status ? '有効' : '無効'}</Text>
          <br />
          <br />

          <Text strong>■ メモ（内部用）</Text>
          <div style={{ height: 4 }} />
          <TextArea
            placeholder="社内メモ（外部には表示されません）"
            value={value.description || ''}
            onChange={onChangeDescription}
            autoSize={{ minRows: 2, maxRows: 6 }}
          />
          <br />
          <br />

          <Text strong>■ プロフィール文（記事ページ等に表示）</Text>
          <div style={{ height: 4 }} />
          <TextArea
            placeholder="講師の自己紹介・経歴等"
            value={value.profile || ''}
            onChange={onChangeProfile}
            autoSize={{ minRows: 6, maxRows: 12 }}
          />
          <br />
          <br />

          <Text strong>■ アイコン画像（記事ページの円形アイコン用）</Text>
          <div style={{ height: 8 }} />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              padding: '12px 16px',
              border: '1px solid var(--grey-20)',
              borderRadius: 4,
              background: 'var(--grey-10)',
            }}
          >
            {value.photo_url ? (
              <Avatar
                size={96}
                src={value.photo_url}
                style={{ border: '1px solid #e8e8e8' }}
              />
            ) : (
              <Avatar size={96} icon={<UserOutlined />} />
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <input
                type="file"
                accept="image/jpeg,image/png"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={onFileSelected}
              />
              <Button onClick={onPickIcon}>
                {value.photo_url ? '画像を変更' : '画像を選択'}
              </Button>
              {value.photo_url ? (
                <Button danger onClick={onRemovePhoto}>
                  画像を削除
                </Button>
              ) : null}
              <Text type="secondary" style={{ fontSize: 12 }}>
                JPEG / PNG、10MB 以下。円形に切り抜けます。
              </Text>
            </div>
          </div>
          <br />
          <br />

          <Button
            type="primary"
            size="large"
            onClick={onSubmit}
            disabled={false}
          >
            保存
          </Button>
          {'　'}
          <Button size="large" onClick={onCancel}>
            キャンセル
          </Button>
        </Form>
      </div>

      {/* 円形クロップモーダル */}
      <Modal
        title="アイコン画像のトリミング"
        open={cropModalOpen}
        onCancel={() => {
          if (uploading) return;
          setCropModalOpen(false);
          setImageSrc(null);
        }}
        onOk={onCropDone}
        okText={uploading ? 'アップロード中…' : '完了'}
        cancelText="キャンセル"
        confirmLoading={uploading}
        maskClosable={!uploading}
        destroyOnHidden
        width={520}
      >
        <div style={{ position: 'relative', width: '100%', height: 360, background: '#333' }}>
          {imageSrc ? (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          ) : null}
        </div>
        <div style={{ marginTop: 16 }}>
          <Text>ズーム</Text>
          <Slider
            min={1}
            max={3}
            step={0.05}
            value={zoom}
            onChange={(v) => setZoom(v)}
            disabled={uploading}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            画像をドラッグして位置を、スライダーまたはホイールでズームを調整してください。
          </Text>
        </div>
      </Modal>
    </Fragment>
  );
};

export default MakeInstructorPage;
